/**
 * PgPubLogout.jsx
 */

import { useState, useEffect, useRef } from 'react';
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import "@esri/calcite-components/components/calcite-loader";

import { CompConfirmLogout } from '@public/confirmLogout/ConfirmLogout';
import { useAuthBiogems } from '@cores/security/hooks/useAuthBiogems';
import {
  AppResource,
  APP_CONSTANTS,
  LOCAL_STORAGE_KEYS
} from '@config/config';

import { clearLocalSession } from '@libs/utils/sessionStorage.js';
import useBogsGisPortalAuth from '@cores/security/hooks/useBogsGisPortalAuth';

import "./PgPubLogout.css";

export const PgPubLogout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAutoLogout =
    searchParams.get('auto') === 'true';

  const urlProvider =
    searchParams.get('provider');

  const {
    provider: contextProvider,
    setAuthState
  } = useAuthBiogems();

  const [showLogoutConfirm, setShowLogoutConfirm] =
    useState(true);

  const [isLoggingOut, setIsLoggingOut] =
    useState(false);

  // Priority: Context → URL parameter → localStorage
  const effectiveProvider =
    contextProvider ||
    urlProvider ||
    localStorage.getItem(
      LOCAL_STORAGE_KEYS?.NIOGEMS?.AUTH_PROVIDER
    );

  const isGisUser =
    effectiveProvider ===
    APP_CONSTANTS.SSO_AUTH;

  const { logout: ssoLogout } =
    useBogsGisPortalAuth(
      true,
      {
        autoInit: isGisUser
      }
    );

  const hasAutoFired = useRef(false);

  const handleConfirm = async () => {
    // Show progress feedback immediately.
    setShowLogoutConfirm(false);
    setIsLoggingOut(true);

    try {
      if (isGisUser) {
        console.log('SSOAuth logout');

        // Keep tokens available until the identity provider logout completes.
        await ssoLogout();
      } else {
        console.log(
          'Legacy Login.gov logout:',
          AppResource.logout_url
        );

        // Browser navigation follows after local session cleanup.
        clearLocalSession();

        setAuthState({
          user: null,
          loading: false,
          currentReservations: [],
          provider: null
        });

        window.location.replace(
          AppResource.logout_url
        );

        return;
      }
    } catch (err) {
      console.error(
        'SSO logout failed; using fallback redirect.',
        err
      );

      // Fallback ensures users do not remain on a stuck logout screen.
      window.location.replace(
        AppResource.logout_url
      );

      return;
    } finally {
      // Runs for the SSO path after ssoLogout resolves/rejects.
      clearLocalSession();

      setAuthState({
        user: null,
        loading: false,
        currentReservations: [],
        provider: null
      });
    }
  };

  useEffect(() => {
    if (
      isAutoLogout &&
      !hasAutoFired.current
    ) {
      hasAutoFired.current = true;
      handleConfirm();
    }
  }, [isAutoLogout]);

  const handleCancel = () => {
    setShowLogoutConfirm(false);
    navigate(-1);
  };

  // Show a full-page loading state during both manual and automatic logout.
  if (
    isLoggingOut ||
    isAutoLogout
  ) {
    return (
      <div
        className="logout-loader-container"
        role="status"
        aria-live="polite"
        aria-label="Logging out"
      >
        <calcite-loader
          active
          label="Logging out"
          text="Logging you out securely..."
          scale="m"
        />

        <p>
          Logging you out securely...
        </p>
      </div>
    );
  }

  return (
    <CompConfirmLogout
      isOpen={showLogoutConfirm}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
};

export default PgPubLogout;